import { UserID } from "../../user/user.interface"
import { PaymentMethodCardBrand } from "../payment/payment-method.types"
import { IUserAddress } from "../../user/user-address.interface"

export interface ICreditCard {
  cardholderName: string
  cardType: CreditCardType
  cardNumber: number | string
  expiryMonth: number | string
  expiryYear: number | string
  /** The 3-digit code on the back of the card */
  signature: number | string
  /** Card data is tokenized and secure proxy should be used to reveal card data */
  tokenId?: string
}

export type TokenizedCreditCardData = {
  tokenId: string
  tokenUpdated?: Date | null
  /** The 3-digit code on the back of the card is expired */
  readonly signatureExpired?: boolean | null
  readonly signatureExpireAt?: Date | null
  brandId: CreditCardTypeCardBrandId
  cardType: CreditCardType
  cardBin: string
  cardLast4: string
  cardholderName: string
  expiryMonth: number
  expiryYear: number
}

export type TokenizedCreditCardID = number

export type TokenizedCreditCard = TokenizedCreditCardData & {
  id: TokenizedCreditCardID
  userId: UserID
  /** Billing address for the card */
  billingAddress?: IUserAddress
  created?: string | Date
  updated?: string | Date
}

export type CreditCardType =
  /** Visa Credit Card */
  | "VIS"
  /** Mastercard */
  | "MSC"
  /** Visa Debit Card */
  | "DEL"
  /** Maestro */
  | "MAE"
  /** Switch */
  | "SWI"
  /** American Express */
  | "AMX"
  /** Electron */
  | "ELC"
  /** Solo */
  | "SOL"

export const CreditCardTypeLabels = new Map<CreditCardType, string>([
  ["VIS", "Visa Credit Card"],
  ["MSC", "Mastercard"],
  ["DEL", "Visa Debit Card"],
  ["MAE", "Maestro"],
  ["SWI", "Switch"],
  ["AMX", "American Express"],
  ["ELC", "Electron"],
  ["SOL", "Solo"],
])

export type CreditCardTypeCardBrandId =
  | "american-express"
  | "diners-club"
  | "discover"
  | "elo"
  | "hiper"
  | "hipercard"
  | "jcb"
  | "maestro"
  | "mastercard"
  | "mir"
  | "unionpay"
  | "visa"

export type CreditCardTypeCardBrandNiceType =
  | "American Express"
  | "Diners Club"
  | "Discover"
  | "Elo"
  | "Hiper"
  | "Hipercard"
  | "JCB"
  | "Maestro"
  | "Mastercard"
  | "Mir"
  | "UnionPay"
  | "Visa"

export type CreditCardTypeSecurityCodeLabel = "CVV" | "CVC" | "CID" | "CVN" | "CVE" | "CVP2"

export const CardBrandNiceTypeMap = new Map<
  CreditCardTypeCardBrandId,
  CreditCardTypeCardBrandNiceType
>([
  ["american-express", "American Express"],
  ["diners-club", "Diners Club"],
  ["discover", "Discover"],
  ["elo", "Elo"],
  ["hiper", "Hiper"],
  ["hipercard", "Hipercard"],
  ["jcb", "JCB"],
  ["maestro", "Maestro"],
  ["mastercard", "Mastercard"],
  ["mir", "Mir"],
  ["unionpay", "UnionPay"],
  ["visa", "Visa"],
])

export const CardPaymentMethodBrandMap = new Map<CreditCardTypeCardBrandId, PaymentMethodCardBrand>(
  [
    ["american-express", "amex"],
    ["diners-club", "diners"],
    ["discover", "discover"],
    // ["elo", "elo"],
    // ["hiper", "hiper"],
    // ["hipercard", "hipercard"],
    ["jcb", "jcb"],
    // ["maestro", "maestro"],
    ["mastercard", "mastercard"],
    // ["mir", "mir"],
    ["unionpay", "unionpay"],
    ["visa", "visa"],
  ],
)

export const cardTypeTitle = (cardType: CreditCardType): string => {
  return CreditCardTypeLabels.get(cardType) ?? cardType
}

export const getInternalCardType = (
  cardBrandId: CreditCardTypeCardBrandId,
): CreditCardType | null => {
  switch (cardBrandId) {
    case "american-express":
      return "AMX"
    // case "diners-club":
    //   return "DIN"
    // case "discover":
    //   return "DIS"
    // case "elo":
    //   return "ELO"
    // case "hiper":
    //   return "HIP"
    // case "hipercard":
    //   return "HPC"
    // case "jcb":
    //   return "JCB"
    case "maestro":
      return "MAE"
    case "mastercard":
      return "MSC"
    // case "mir":
    //   return "MIR"
    // case "unionpay":
    //   return "UNP"
    case "visa":
      return "VIS"
    default:
      return null
  }
}

/**
 * Function to generate a masked card number based on card type gaps
 * @param {string} bin
 * @param {string} last4
 * @param {number} cardLength
 * @param {number[]} gaps
 * @return {string}
 */
export const maskCardNumber = (
  bin: string,
  last4: string,
  cardLength: number,
  gaps: number[],
): string => {
  // Generate the middle part length (total length - bin - last4)
  const middleLength = cardLength - bin.length - last4.length
  const maskedMiddle = "•".repeat(middleLength)

  // Combine the bin, middle masked, and last 4 digits
  const fullMaskedNumber = `${bin}${maskedMiddle}${last4}`

  // Insert gaps into the masked card number based on the gap positions
  let formattedMasked = ""
  let lastIndex = 0

  // Go through the gaps and insert spaces at the specified positions
  gaps.forEach((gap) => {
    formattedMasked += fullMaskedNumber.slice(lastIndex, gap) + " "
    lastIndex = gap
  })

  // Add the remaining part of the masked number after the last gap
  formattedMasked += fullMaskedNumber.slice(lastIndex)

  // Return the formatted result
  return formattedMasked.trim()
}
